/**
 * FireGuard Live - Socket.IO Service
 * Real-time комуникация с backend
 */
import { io } from 'socket.io-client';
import { API_ORIGIN } from './api';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.listeners = new Map();
  }

  /**
   * Свържи се към Socket.IO сървъра
   */
  connect() {
    const token = localStorage.getItem('fireguard_token');
    
    if (!token) {
      console.warn('No token found, cannot connect to socket');
      return;
    }

    const serverUrl = process.env.REACT_APP_WS_URL || API_ORIGIN;
    
    this.socket = io(serverUrl, {
      auth: {
        token: token
      },
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 20000
    });

    this.setupEventHandlers();
    
    return this.socket;
  }

  /**
   * Настрой основните event handlers
   */
  setupEventHandlers() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('✅ Connected to FireGuard Socket.IO server');
      this.connected = true;
      this.emit('socket:connected', true);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Disconnected from Socket.IO server:', reason);
      this.connected = false;
      this.emit('socket:connected', false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket.IO connection error:', error);
      this.emit('socket:error', error);
    });

    // Server response events
    this.socket.on('connected', (data) => {
      console.log('🚀 FireGuard connection established:', data);
      this.emit('socket:ready', data);
    });

    this.socket.on('error', (error) => {
      console.error('❌ Socket server error:', error);
      this.emit('socket:error', error);
    });

    // Real-time data events
    this.socket.on('new-alert', (alert) => {
      console.log('🚨 New alert received:', alert);
      this.emit('alert:new', alert);
    });

    this.socket.on('critical-alert', (alert) => {
      console.log('🔥 Critical alert received:', alert);
      this.emit('alert:critical', alert);
    });

    this.socket.on('device-alert', (alert) => {
      console.log('📱 Device alert received:', alert);
      this.emit('device:alert', alert);
    });

    this.socket.on('maintenance-update', (maintenance) => {
      console.log('🔧 Maintenance update received:', maintenance);
      this.emit('maintenance:update', maintenance);
    });

    this.socket.on('task-update', (task) => {
      console.log('📋 Task update received:', task);
      this.emit('task:update', task);
    });

    this.socket.on('device-status-change', (device) => {
      console.log('📊 Device status change:', device);
      this.emit('device:status-change', device);
    });

    this.socket.on('device-status-update', (update) => {
      console.log('📈 Device status update:', update);
      this.emit('device:status-update', update);
    });

    this.socket.on('inspection-update', (inspection) => {
      console.log('🔍 Inspection update received:', inspection);
      this.emit('inspection:update', inspection);
    });

    this.socket.on('dashboard-update', (data) => {
      console.log('📊 Dashboard update received:', data);
      this.emit('dashboard:update', data);
    });

    this.socket.on('system-status-update', (status) => {
      console.log('⚙️ System status update:', status);
      this.emit('system:status-update', status);
    });

    // Room events
    this.socket.on('room-joined', (data) => {
      console.log('🏠 Joined room:', data);
      this.emit('room:joined', data);
    });

    this.socket.on('device-monitoring', (data) => {
      console.log('👁️ Device monitoring started:', data);
      this.emit('device:monitoring', data);
    });

    // Health check
    this.socket.on('pong', (data) => {
      this.emit('socket:pong', data);
    });
  }

  /**
   * Изпрати събитие към сървъра
   */
  send(event, data) {
    if (this.socket && this.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('Socket not connected, cannot send:', event);
    }
  }

  /**
   * Присъедини се към стая
   */
  joinRoom(room) {
    this.send('join-room', room);
  }

  /**
   * Напусни стая
   */
  leaveRoom(room) {
    this.send('leave-room', room);
  }

  /**
   * Започни мониторинг на устройство
   */
  monitorDevice(deviceId) {
    this.send('monitor-device', deviceId);
  }

  /**
   * Спри мониторинг на устройство
   */
  stopMonitoringDevice(deviceId) {
    this.send('stop-monitor-device', deviceId);
  }

  /**
   * Абонирай се за maintenance обновления
   */
  subscribeToMaintenance() {
    this.send('subscribe-maintenance');
  }

  /**
   * Заявка за dashboard обновление
   */
  requestDashboardUpdate() {
    this.send('request-dashboard-update');
  }

  /**
   * Заявка за статус на устройство
   */
  requestDeviceStatus(deviceId) {
    this.send('request-device-status', deviceId);
  }

  /**
   * Потвърди алерт
   */
  acknowledgeAlert(alertId) {
    this.send('acknowledge-alert', alertId);
  }

  /**
   * Ping test
   */
  ping() {
    this.send('ping');
  }

  /**
   * Добави listener за събитие
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  /**
   * Премахни listener за събитие
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  /**
   * Изпрати събитие към всички listeners
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in socket listener:', error);
        }
      });
    }
  }

  /**
   * Прекъсни връзката
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.listeners.clear();
      console.log('🔌 Socket.IO disconnected');
    }
  }

  /**
   * Провери дали е свързан
   */
  isConnected() {
    return this.connected && this.socket && this.socket.connected;
  }

  /**
   * Получи socket instance
   */
  getSocket() {
    return this.socket;
  }
}

// Създай singleton instance
const socketService = new SocketService();

export default socketService;